import {ChangeDetectionStrategy, Component, input, signal} from "@angular/core"
import {MatTooltipModule} from "@angular/material/tooltip"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress"
import {DisableButtonAfterClickDirective} from "@app/common/directives"

/**
 * One publishing target, as one button whose icon *is* its state.
 *
 * Deliberately not a variant of `MaterialOutputButtonComponent`, which this borrows its visual
 * language from. That component is built around an artifact you download: its terminal state is
 * `complete`, it renders a download arrow, and its actions are download / rename / delete. A publish
 * has no artifact — its terminal state is a fact about a remote system, and its actions are publish /
 * republish / retry. Adding a fifth variant there would put two vocabularies in one union and hand
 * every existing call site a case it can never reach.
 *
 * What is shared is how it looks — the same triangle, spinner and update marker — so the outputs and
 * publishing sections read as one system. Only the state model is separate.
 */
export type MaterialOutputPublishingButtonProps = {
    /** The target, e.g. "Configura". Not the action — the icon says that. */
    label: string
    icon?: string
    tooltip: string
} & (
    | {
          /** Publishable now. `republish` additionally marks that the remote holds an older revision. */
          state: "publish" | "republish" | "retry"
          onTrigger: () => void
      }
    /** Nothing to do, and a reason worth reading — the message comes from the publish-state classifier. */
    | {state: "blocked"}
    | {state: "inFlight"}
    /** The remote holds the current revision. No action: re-publishing it is refused server-side. */
    | {state: "upToDate"}
)

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-output-publishing-button",
    imports: [DisableButtonAfterClickDirective, LoadingSpinnerIconComponent, MatTooltipModule],
    templateUrl: "./material-output-publishing-button.component.html",
    styleUrl: "./material-output-publishing-button.component.scss",
})
export class MaterialOutputPublishingButtonComponent {
    readonly $props = input.required<MaterialOutputPublishingButtonProps>({alias: "props"})

    // Mirrors the outputs button: a tooltip on a nested control fights the outer one.
    readonly $outerTooltipDisabled = signal(false)
}
